// Feed Item 
'use client';
import { IAdaptiveActionCard, IContentModel } from '@ninjacart/nc-nk-core';
import {
  KVPair,
  UserProps,
  extractYouTubeId,
  useAppPersistenceStore,
  useFeedStore,
  useHomeContext,
  useRecommendedProducts,
} from '@ninjacart/nc-nk-store';
import {
  AnimatePresence,
  Overlay,
  Spinner,
  Stack,
  useTheme,
  VideoPlayIconRegular,
  View,
  YStack,
} from '@ninjacart/nc-nk-ui';
import { useRouter } from '@ninjacart/nc-nk-web-deps';
// import { type FlashList } from '@shopify/flash-list';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, GestureResponderEvent } from 'react-native';

import { useAdManager } from '../../hooks/useAdManager';
import { useEventTracking } from '../../hooks/useEventTracking';
import { useVideoPlayer } from '../../hooks/useVideoPlayer';
import { IHomeListItemRef } from '../../types/adaptiveInterface';
import { FeedVideoCard, Thumbnail } from '../feedVideo';
import { ListFooter } from '../homeScreen/footer';
import { ListHeader } from '../homeScreen/header';
import { ListTextContent } from '../homeScreen/listTextContent';
// import { RecommendedProductsNudgeComponent } from '../products/productNudge/recommendedProductNudge';
import { Link } from 'solito/link';

interface IHomeStreamListItem {
  item?: IAdaptiveActionCard;
  index: number;
  userProfile?: UserProps;
  flashListRef?: any | null;
  screenDimensions: any;
  pageName?: string;
  updateConnectionStatus?: Function;
  onReportBottomSheetOpen: Function;
  onCommentBottomSheetOpen: (item: IContentModel, index: number) => void;
  handleOnAutoPlayExpand?: (
    isExpanded: boolean,
    indexToExpand: number,
    videoId: number | string
  ) => void;
  adSizeDefsPreloaded?: KVPair;
  bannerAdKeywords?: string[];
  deviceWidth?: number;
  isAutoPlayBannerAdEnabled: boolean;
  setActiveFeedItemIndex?: (index: number) => void;
}

// Helper function to normalize item data and detect YouTube videos based on targetUrl
const normalizeItem = (item: any) => {
  const content = item?.actionData?.content || {};
  const youtubeId = content?.targetUrl ? extractYouTubeId(content.targetUrl) : null;

  return {
    ...item,
    type: youtubeId ? 'youtube' : 'stream',
    actionData: {
      ...item.actionData,
      content: {
        ...content,
        isYouTube: !!youtubeId,
        youtubeVideoId: youtubeId,
      },
    },
  };
};

export const FeedItem = forwardRef<IHomeListItemRef, IHomeStreamListItem>(
  (
    {
      item: propItem,
      index,
      pageName,
      updateConnectionStatus,
      handleOnAutoPlayExpand,
      isAutoPlayBannerAdEnabled,
      onReportBottomSheetOpen,
      onCommentBottomSheetOpen,
    },
    ref
  ) => {
  const item = normalizeItem(propItem);
    const { push } = useRouter();
    const theme = useTheme();
    const { t } = useTranslation();
    const homeContextFields = useHomeContext([
      'adSizeDefsPreloaded',
      'bannerAdKeywords',
      'videoFeedIndex',
    ]);
    // const activeVideoFeedIndex = homeContextFields.videoFeedIndex.get as number;
    // console.log('🚀 ~ List item activeVideoFeedIndex:', activeVideoFeedIndex);
    // const isMomentumScrollEnded = homeContextFields.isMomentumScrollEnded.get as boolean;
    // const canAutoPlayVideo = activeVideoFeedIndex === index && isMomentumScrollEnded;
    const adSizeDefsPreloaded = homeContextFields.adSizeDefsPreloaded.get as KVPair | undefined;
    const bannerAdKeywords = homeContextFields.bannerAdKeywords.get as string[] | undefined;
    const deviceWidth = Dimensions.get('window').width;
    // const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const setLongVideoSelected = useFeedStore((state) => state.setLongVideoSelected);
    const { recommendedProducts, fetchRecommendedProducts } = useRecommendedProducts();
    const isVideoFeedActive = index === (homeContextFields.videoFeedIndex.get as number);
    const [isRequestCompanionAd, setIsRequestCompanionAd] = useState(false);

    // Update videoHref based on video type
    // const getVideoHref = () => {
    //   const content = item.actionData?.content;

    //   if (content?.isYouTube && content?.youtubeVideoId) {
    //     // For YouTube videos: Use numeric ID in the path, YouTube ID in query params
    //     return `/${content?.languageCode ?? 'en'}/v/${content.seoUrl || content.id}?type=youtube&youtubeVideoId=${content.youtubeVideoId}&itemIndex=${content.itemIndex || index}&eventId=${content.eventId || ''}`;
    //   } else {
    //     // For regular videos: Use numeric ID in the path
    //     const videoId = content?.id || content?.videoId;
    //     return `/${content?.languageCode ?? 'en'}/v/${content?.seoUrl || videoId}?type=stream&itemIndex=${content.itemIndex || index}&eventId=${content.eventId || ''}`;
    //   }
    // };

    // const videoHref = getVideoHref();

    const videoHref = `/watch?v=${item?.actionData?.content?.id}`;
    // const videoHref = `/${item?.actionData?.content?.languageCode ?? 'en'}/v/${item?.actionData?.content?.seoUrl ?? item?.actionData?.content?.id}`;
    // console.log('🚀 ~ isVideoFeedActive:', isVideoFeedActive, index);
    // const videoHeight = 250;
    // const height = useSharedValue(videoHeight);
    const {
      isExpanded,
      setIsExpanded,
      // animatedVideoHeight,
      // animatedContainerHeight,
      // animatedAdMarginTop,
      // scale,
      currentInfoRef,
      adTagUrl,
      resetVideoState,
    } = useVideoPlayer(item, index, isVideoFeedActive, handleOnAutoPlayExpand);

    const [showThumbnail, setShowThumbnail] = useState(true);
    const [isVideoReadyForPlay, setIsVideoReadyForPlay] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { renderAd } = useAdManager(
      isVideoFeedActive,
      item?.actionData?.content?.itemKey ?? index.toString(),
      item?.actionData?.content?.adSizeDefs,
      adSizeDefsPreloaded,
      bannerAdKeywords,
      (newHeight) => {
        // console.log('🚀 ~ newHeight:', newHeight);
        setIsExpanded(true);
        // animatedContainerHeight.value = newHeight;
        // animatedAdMarginTop.value = 12;
      }
    );

    const { trackItemClick } = useEventTracking(pageName);

    useImperativeHandle(ref, () => ({
      resetAutoPlay: () => {
        resetVideoState();
        // setActiveFeedItemIndex?.(-1);
      },
    }));

    const onFeedItemPress = (event: GestureResponderEvent): void => {
      const videoPlayerTime = trackItemClick(item, currentInfoRef.current.currentTime);

      const adaptiveActionFormat: IAdaptiveActionCard = {
        ...item,
        actionData: {
          ...item.actionData,
          contentType: 'json',
          content: {
            ...item.actionData.content,
            ...videoPlayerTime,
          },
          mediaDetails: item.actionData.mediaDetails || [],
          props: item.actionData.props || [],
          eventNamePrefix: item.actionData.eventNamePrefix || '',
        },
      };

      setLongVideoSelected(adaptiveActionFormat);
      push(`/longVideo/${item.actionData?.content?.id}?adDelay=true`);
    };

      // return (
    //   <View flex={1} height={200}>
    //     <Text>{item.actionData?.content?.title}</Text>
    //     <Text>{`\n`}</Text>
    //     {/* <Text>{item.actionData?.content?.description}</Text> */}
    //   </View>
    // );

    // console.log('Item title => ', item.actionData?.content?.id);

    // return (
    //   <div style={{ minHeight: 200, height: 200, flex: 1 }}>
    //     <p>{item.actionData?.content?.title}</p>
    //     <p>{`\n`}</p>
    //   </div>
    // );
    
    // Get thumbnail URL based on video type
    const getThumbnailUrl = () => {
      if (item.actionData?.content?.isYouTube) {
        return `https://img.youtube.com/vi/${item.actionData.content.youtubeVideoId}/hqdefault.jpg`;
      }
      return (
        item.actionData?.content?.thumbnailUrl || item.actionData?.mediaDetails?.[0]?.preview_url
      );
    };

    const thumbnailUrl = getThumbnailUrl();

    return (
      <View flex={1} backgroundColor={'$cardbackground'} minHeight={300}>
        <View flex={1}>
          {/* Header */}
          <AnimatePresence>
            <ListHeader
              content={item.actionData.content}
              index={index}
              pageName={pageName}
              onReportBottomSheetOpen={onReportBottomSheetOpen}
              updateConnectionStatus={updateConnectionStatus}
            />
          </AnimatePresence>

          {/* Video Card */}
          <View flex={1} marginBottom={8} minHeight={240} maxHeight={240} width={'100vw'}>
            {!pageName && (
              <FeedVideoCard
                item={item}
                index={index}
                isVideoFeedActive={isVideoFeedActive}
                duration={item?.actionData?.content?.duration}
                currentInfoRef={currentInfoRef}
                onAutoPlayModeExpandCallback={setIsExpanded}
                height={{}}
                adTagurl={adTagUrl}
                pageName={pageName}
                deviceWidth={deviceWidth}
                isExpanded={isExpanded}
                isAutoPlayBannerAdEnabled={isAutoPlayBannerAdEnabled}
                setLongVideoSelected={setLongVideoSelected}
                setIsRequestCompanionAd={setIsRequestCompanionAd}
                setIsLoading={setIsLoading}
                setIsVideoReadyForPlay={setIsVideoReadyForPlay}
                setShowThumbnail={setShowThumbnail}
              />
            )}
            <AnimatePresence>
              {showThumbnail && (
                <Overlay
                  animation="quick"
                  enterStyle={{ opacity: 1 }}
                  exitStyle={{ opacity: 0 }}
                  opacity={1}
                  maxHeight={240}
                  height={240}
                  width={'100vw'}
                  maxWidth={600}
                  zIndex={10}
                >
                  <Thumbnail
                    thumbnailUrl={thumbnailUrl}
                    isVideoFeedActive={isVideoFeedActive}
                    isLoading={isLoading}
                    isVideoReadyForPlay={isVideoReadyForPlay}
                    onPress={onFeedItemPress}
                    videoHeight={240}
                    priority={index < 3}
                    loading={index < 2 ? 'eager' : 'lazy'}
                  />
                  <YStack
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="rgba(0,0,0,0.2)"
                    pointerEvents="none"
                  >
                    {!isVideoFeedActive && (
                      <VideoPlayIconRegular size={50} color={theme['background']?.val} />
                    )}
                    {isVideoFeedActive && !isVideoReadyForPlay && (
                      <Spinner size="large" color="$primary" />
                    )}
                  </YStack>
                </Overlay>
              )}
            </AnimatePresence>
          </View>

                    {/* <YStack flex={1} alignItems="center" justifyContent="center">
            {!isRequestCompanionAd && isVideoFeedActive && (
              <Placeholder Animation={ShineOverlay}>
                <PlaceholderLine
                  style={[
                    {
                      width: 320,
                      height: 50,
                      borderRadius: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      alignContent: 'center',
                    },
                  ]}
                />
              </Placeholder>
            )}
          </YStack> */}

          {isRequestCompanionAd && <AnimatePresence>{renderAd([[300, 50]])}</AnimatePresence>}

          {/* Text */}
          <View paddingVertical={4}>
            <ListTextContent content={item.actionData.content} maxLength={100} />
          </View>

          {/* Product Nudge */}
          {/* {recommendedProducts && recommendedProducts?.length > 0 && (
            <View flex={1} padding={10}>
              <RecommendedProductsNudgeComponent
                products={recommendedProducts}
                t={t}
                sourcePage={'home Page'}
              />
            </View>
          )} */}

          {/* Footer */}
          {!pageName && (
            <>
              <Stack
                borderTopColor="$feedBorderLineColor"
                borderTopWidth={1}
                marginHorizontal={8}
              />
              <ListFooter
                content={item.actionData.content}
                itemType={item.type}
                index={index}
                uniqueDeviceId={useAppPersistenceStore((state) => state.deviceId)}
                onCommentBottomSheetOpen={onCommentBottomSheetOpen}
                // userProfile={{}}
              />
            </>
          )}
        </View>
      </View>
    );
  }
);
